/**
 * From a search form to the results page — on the newest build.
 *
 * A tab or home-screen app opened before a deploy keeps running the old
 * JavaScript (see src/lib/buildVersion.js), so a customer could press Search
 * on yesterday's build and never see today's changes. The banner only offers
 * a refresh; here the search itself updates the app: when a newer build is
 * live, pressing Search does a full page load of the results URL instead of
 * an in-app navigation. Everything the search needs travels in that URL, so
 * nothing the customer chose is lost — the fresh page reads it and searches.
 *
 * "Is a newer build live?" comes from the last /api/health check, which
 * checkBuildWhileSearching() refreshes while a search is being filled in, so
 * the answer is current when Search is pressed and the press never waits on
 * the network.
 *
 * Never in the iOS app: its code ships inside the app (a reload would load
 * the same build) and it updates itself. And at most once per deploy per
 * tab: should the fresh page still read as old (an index.html served from a
 * cache, say), the next press just searches.
 */
import { isNativeApp } from '@/native/bootstrap';
import { buildFlightSearchParams } from '@/lib/searchPrefetch';
import { lastDeployedCommit, newerBuildIsLive, refreshDeployedCommit } from '@/lib/buildVersion';
import { writePassengerParams } from '@/utils/passengerTypes';

/** How old the answer may be while a search is being filled in. */
export const SEARCH_BUILD_CHECK_MAX_AGE_MS = 10_000;
const LOADED_FOR_KEY = 'defera.searchUpdate.loadedFor';

const sessionStore = () => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

/** Refresh "is a newer build live?" in the background (throttled, never throws). */
export function checkBuildWhileSearching({ native = isNativeApp } = {}) {
  if (native) return;
  refreshDeployedCommit({ maxAgeMs: SEARCH_BUILD_CHECK_MAX_AGE_MS });
}

/**
 * When a newer build is live, load `url` in full — bringing the new build —
 * and return true. Otherwise return false and leave the navigation to the
 * caller.
 */
export function loadNewBuildForSearch(url, { native = isNativeApp, location, storage } = {}) {
  if (native || !url || !newerBuildIsLive()) return false;
  const store = storage === undefined ? sessionStore() : storage;
  const page = location || (typeof window !== 'undefined' ? window.location : null);
  // Without storage there is no way to stop a reload loop, so don't start one.
  if (!store || !page) return false;
  const commit = lastDeployedCommit();
  try {
    if (store.getItem(LOADED_FOR_KEY) === commit) return false;
    store.setItem(LOADED_FOR_KEY, commit);
  } catch {
    return false;
  }
  page.assign(url);
  return true;
}

/** Open the results page at `url`, on the newest build. */
export function openSearchResults(navigate, url) {
  if (!loadNewBuildForSearch(url)) navigate(url);
}

/**
 * The results-page URL for what a search form holds, or null while it isn't
 * a complete, valid search. Same parameters the home page sends and the
 * results page reads back (GoogleFlightsSearch's URL prefill).
 */
export function resultsPageUrl({ origin, destination, from, to, passengers, cabinClass, tripMode, paymentMethod }) {
  const search = buildFlightSearchParams({ origin, destination, from, to, passengers, cabinClass });
  if (!search) return null;
  const query = new URLSearchParams();
  query.set('origin', search.slices[0].origin);
  query.set('destination', search.slices[0].destination);
  query.set('departureDate', search.slices[0].departure_date);
  if (search.slices[1]) query.set('returnDate', search.slices[1].departure_date);
  writePassengerParams(query, search.passengers);
  query.set('cabin', search.cabin_class);
  if (tripMode) query.set('tripMode', tripMode);
  if (paymentMethod) query.set('paymentMethod', paymentMethod);
  return `/search?${query.toString()}`;
}
